import { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Modal } from '@/components/common/modal';
import { Button } from '@/components/form/button';
import { SignedInMethod, useAuth } from '@/features/auth';

import { PitchRateContext } from '../provider/provider';
import {
  ActionKind,
  CalculationMethod,
  PlatoGravity,
  WortUnits,
} from '../provider/types';

interface IProps {
  /** based on the state, modal will be rendered */
  isOpen: boolean;

  /** toggle modal open and close state */
  toggle: () => void;

  /** callback on save */
  onSave: () => void;
}

const wortVolumeLabelMatrix = {
  [SignedInMethod.Wine]: 'Must',
  [SignedInMethod.Beer]: 'Wort',
  [SignedInMethod.Ethanol]: '',
};

export const PitchRateSettingsModal: React.FC<IProps> = ({ isOpen, toggle, onSave }) => {
  const { user } = useAuth();
  const { state, dispatch } = useContext(PitchRateContext);

  // wort or must terms changed based on type of analysis
  const wortLabel = user ? wortVolumeLabelMatrix[user?.signedInMethod] : '';

  const [localState, setLocalState] = useState<{
    wortUnit: WortUnits;
    gravityType: PlatoGravity;
    calculationMethod: CalculationMethod;
  }>({
    wortUnit: state.wortUnit,
    gravityType: state.gravityType,
    calculationMethod: state.calculationMethod,
  });

  const handleWortUnitChange = (payload: WortUnits) => {
    dispatch({ type: ActionKind.ChangeWortVolumeUnit, payload });
  };

  const handlePlotGravityChange = (payload: PlatoGravity) => {
    dispatch({ type: ActionKind.ChangePlotGravity, payload });
  };

  const handleCalculationMethodChange = (payload: CalculationMethod) => {
    dispatch({ type: ActionKind.ChangeCalculationMethod, payload });
  };

  const updateLocalState = (payload: {
    wortUnit?: WortUnits;
    gravityType?: PlatoGravity;
    calculationMethod?: CalculationMethod;
  }) => {
    setLocalState({ ...localState, ...payload });
  };

  const handleOnSave = () => {
    handleWortUnitChange(localState.wortUnit);
    handlePlotGravityChange(localState.gravityType);
    handleCalculationMethodChange(localState.calculationMethod);
    onSave();
    toggle();
  };

  return (
    <Modal
      isOpen={isOpen}
      toggle={toggle}
      title="Pitch Calculator Settings"
      conatinerClassnames="w-full md:w-5/6 lg:w-2/3 xl:w-2/3 2xl:w-1/2"
    >
      <div className="flex flex-col gap-4 mt-6">
        <div className="flex flex-col gap-2">
          <h2 className="text-lg">{wortLabel} Volume Unit</h2>
          <RadioInput
            name="wortUnit"
            label="Hectoliter [hl]"
            value={WortUnits.Hectorliter}
            checked={localState.wortUnit === WortUnits.Hectorliter}
            onChange={() => updateLocalState({ wortUnit: WortUnits.Hectorliter })}
          />
          <RadioInput
            label="Liter [l]"
            name="wortUnit"
            value={WortUnits.Liter}
            checked={localState.wortUnit === WortUnits.Liter}
            onChange={() => updateLocalState({ wortUnit: WortUnits.Liter })}
          />
          <RadioInput
            label="Gallon [gal]"
            name="wortUnit"
            value={WortUnits.Gallon}
            checked={localState.wortUnit === WortUnits.Gallon}
            onChange={() => updateLocalState({ wortUnit: WortUnits.Gallon })}
          />
          <RadioInput
            label="Barrels [bbl]"
            name="wortUnit"
            value={WortUnits.Barrels}
            checked={localState.wortUnit === WortUnits.Barrels}
            onChange={() => updateLocalState({ wortUnit: WortUnits.Barrels })}
          />
        </div>

        {user?.signedInMethod === SignedInMethod.Beer ? (
          <div className="flex flex-col gap-2">
            <h2 className="text-lg">Plato or Specific Gravity</h2>
            <RadioInput
              label="Plato [°P]"
              name="gravity"
              value={PlatoGravity.Plato}
              checked={localState.gravityType === PlatoGravity.Plato}
              onChange={() => updateLocalState({ gravityType: PlatoGravity.Plato })}
            />
            <RadioInput
              name="gravity"
              label="Specific Gravity"
              value={PlatoGravity.Gravity}
              checked={localState.gravityType === PlatoGravity.Gravity}
              onChange={() => updateLocalState({ gravityType: PlatoGravity.Gravity })}
            />
          </div>
        ) : null}

        <div className="flex flex-col gap-2">
          <h2 className="text-lg">Calculation Method</h2>
          <RadioInput
            name="method"
            value={CalculationMethod.ExcludingYeastVolume}
            checked={
              localState.calculationMethod === CalculationMethod.ExcludingYeastVolume
            }
            onChange={() =>
              updateLocalState({
                calculationMethod: CalculationMethod.ExcludingYeastVolume,
              })
            }
            label={`The ${wortLabel.toLowerCase()} volume you enter does not include the yeast volume`}
          />
          <RadioInput
            name="method"
            value={CalculationMethod.IncludingYeastVolume}
            checked={
              localState.calculationMethod === CalculationMethod.IncludingYeastVolume
            }
            onChange={() =>
              updateLocalState({
                calculationMethod: CalculationMethod.IncludingYeastVolume,
              })
            }
            label={`The ${wortLabel.toLowerCase()} volume you enter includes the yeast volume`}
          />
        </div>

        <div className="flex justify-end">
          <Button onClick={handleOnSave}>Save</Button>
        </div>
      </div>
    </Modal>
  );
};

const RadioInput = ({
  name,
  label,
  value,
  onChange,
  checked = false,
}: {
  name: string;
  label: string;
  value: string;
  checked?: boolean;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
}) => {
  const { t } = useTranslation();

  return (
    <div className="flex gap-4 items-center">
      <input
        name={name}
        type="radio"
        value={value}
        checked={checked}
        onChange={onChange}
        id={`${name}-${value}`}
        className="cursor-pointer"
      />
      <label className="text-gray-700 cursor-pointer" htmlFor={`${name}-${value}`}>
        {t(label)}
      </label>
    </div>
  );
};
